import { Injectable } from '@angular/core';
import { environment } from '@env';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '../services/shared/shared.service';
import { ToastService } from '../services/toast/toast.service';
import axios from 'axios';
import { LoadingService } from '../services/loading/loading.service';
import { LoginAccountService } from '../services/login-account/login-account.service';
import { LoginAccount } from '@app/shared/models/loginAccount';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NavController } from '@ionic/angular';
import { CustomHttpHeader, QueryParams } from '@app/shared/models/userBalance';
import { AlertService } from '../services/alert/alert.service';
import { NavigationExtras } from '@angular/router';
import { API_END_POINT } from '../constants/app-constant';

@Injectable({
  providedIn: 'root',
})
export class AxiosService {
  graphQlEndPoint = environment.apiBaseUrl.replace('/public/', '') + '/graphql';
  apiKey = environment.apiKey;
  subdomain: string;
  tier: string;
  constructor(
    private sharedSvc: SharedService,
    public translateService: TranslateService,
    private toastSvc: ToastService,
    private loadingSvc: LoadingService,
    private loginAccountService: LoginAccountService,
    private navCtrl: NavController,
    private alertService: AlertService
  ) {}

  async httpPost(endPoint: string, body, showLoader = true) {
    if (showLoader) {
      await this.loadingSvc.presentForLongTime('LOADING');
    }
    const baseURL = environment.apiBaseUrl + endPoint;
    return await this.serviceCall({
      method: 'POST',
      url: baseURL,
      data: body,
    }).finally(() => {
      if (showLoader) {
        this.loadingSvc.dismiss();
      }
    });
  }

  serviceCallCustomHeader(requestConfig, header: any, accountId: number) {
    const instance = axios.create({
      baseURL: this.graphQlEndPoint,
    });

    // Request hanlder using interceptor

    instance.interceptors.request.use(
      async (config) => {
        const activeAccount = await this.loginAccountService.getAccountById(accountId);
        if (!accountId || !activeAccount) {
          config.headers = {
            ...header,
            apiKey: this.apiKey,
          };
          return config;
        } else {
          return await this.requestHandler(config, activeAccount, true);
        }
      },
      (error) => Promise.reject(error)
    );

    // Response hanlder using interceptor

    instance.interceptors.response.use(
      async (res) => {
        const { data, errors } = res.data;
        if (errors) {
          this.showErrorToast(errors);
        }
        return Promise.resolve(data);
      },
      async (error) => {
        const { data } = error.response;
        this.showErrorToast(data?.errors || data);
        return Promise.reject(error);
      }
    );
    return instance(requestConfig);
  }

  async postByCustomHeader(
    graphQlQuery: string,
    operationName: string,
    variable,
    showLoader = true,
    header: CustomHttpHeader,
    accountId: number
  ): Promise<any> {
    if (showLoader) {
      await this.loadingSvc.presentForLongTime('LOADING');
    }
    let query: QueryParams = {
      query: graphQlQuery,
      variables: variable || {},
    };
    if (operationName) {
      query.operationName = operationName;
    }
    return await this.serviceCallCustomHeader(
      {
        method: 'POST',
        url: this.graphQlEndPoint,
        data: query,
      },
      header,
      accountId
    ).finally(() => {
      if (showLoader) {
        this.loadingSvc.dismiss();
      }
    });
  }

  async post(
    graphQlQuery: string,
    operationName: string,
    variable?,
    showLoader = true,
    dismissLoader = true
  ): Promise<any> {
    if (showLoader) {
      await this.loadingSvc.presentForLongTime('LOADING');
    }
    const query = {
      operationName,
      variables: variable || {},
      query: graphQlQuery,
    };
    return await this.serviceCall({
      method: 'POST',
      url: this.graphQlEndPoint,
      data: query,
    }).finally(() => {
      if (showLoader && dismissLoader) {
        this.loadingSvc.dismiss();
      }
    });
  }

  async serviceCall(requestConfig) {
    const instance = axios.create({
      baseURL: this.graphQlEndPoint,
    });

    // Request hanlder using interceptor

    instance.interceptors.request.use(
      async (config) => {
        const activeAccount: LoginAccount = await this.loginAccountService.getActivatedAccount();
        return await this.requestHandler(config, activeAccount);
      },
      (error) => Promise.reject(error)
    );

    // Response hanlder using interceptor

    instance.interceptors.response.use(
      async (res) => {
        const { data, errors } = res.data;
        if (errors) {
          this.showErrorToast(errors);
        }
        return Promise.resolve(data);
      },
      async (error) => {
        const { data } = error.response;
        this.showErrorToast(data?.errors || data);
        return Promise.reject(error);
      }
    );
    return instance(requestConfig);
  }

  async requestHandler(config, activeAccount: LoginAccount, isCustomHeader = false) {
    if (this.sharedSvc.getCustomerDetails) {
      this.subdomain = this.sharedSvc.getCustomerDetails.customerData.DomainName;
      this.tier = this.sharedSvc.getCustomerDetails.customerData.Tier;
    }
    let header = {
      tier: this.tier,
      apiKey: this.apiKey,
      subdomain: this.subdomain,
    };

    if (!environment.isTest && activeAccount?.ssoConfirmationResp) {
      let token = activeAccount?.ssoConfirmationResp?.token;
      const refreshToken = activeAccount?.ssoConfirmationResp?.refreshToken;
      const accounts = await this.loginAccountService.read();
      const helper = new JwtHelperService();
      const isTokenExpired = await helper.isTokenExpired(token);
      const isRefreshTokenExpired = await helper.isTokenExpired(refreshToken);

      if (isTokenExpired && isRefreshTokenExpired) {
        await this.handleAccountExpired(isCustomHeader, activeAccount);
        return;
      }
      if (isTokenExpired) {
        // Update refresh token default account
        try {
          const res = await this.getRefreshToken(refreshToken);
          if (res?.data) {
            token = res.data.token;
            activeAccount.ssoConfirmationResp = {
              ...activeAccount.ssoConfirmationResp,
              ...res.data,
            };
            const index = accounts.findIndex((data) => {
              return isCustomHeader ? data.id === activeAccount.id : data.activeAccount;
            });
            accounts[index] = activeAccount;
            await this.loginAccountService.update(accounts);
          }
        } catch (error) {
          console.log('error =====>', error);
        }
      }

      header['Authorization'] = token;
    }
    config.headers = header;
    return config;
  }

  async showErrorToast(error) {
    const e = error?.length ? error[0]?.message : undefined;
    await this.toastSvc.presentErrorToast(e || error?.error?.message || error);
  }

  async getRefreshToken(refreshToken) {
    let header = {
      tier: this.tier,
      apiKey: this.apiKey,
    };
    // const response = await axios.put(environment.apiBaseUrl + 'refreshToken', { token: refreshToken }, { headers: header });
    const response = await axios.put(
      API_END_POINT.refreshToken,
      { token: refreshToken },
      { headers: header }
    );
    return response.data;
  }

  async handleAccountExpired(isCustomHeader: boolean, activeAccount: LoginAccount) {
    try {
      const accounts = await this.loginAccountService.read();
      const list = accounts.filter((data) => {
        return isCustomHeader ? data.id !== activeAccount.id : !data.activeAccount;
      });

      await this.loginAccountService.update(list);
      this.alertService
        .accountExpire(this.translateService.instant('ACCOUNT_EXPIRED'))
        .then(async (result) => {
          if (result) {
            const accountIndex = accounts.findIndex((data) => {
              return isCustomHeader ? data.id === activeAccount.id : data.activeAccount;
            });
            const account = accounts[accountIndex];
            delete account.ssoConfirmationResp;
            const navigationExtras: NavigationExtras = {
              state: { data: account, accountIndex, accountLock: true },
            };
            await this.navCtrl.navigateRoot('app/add-files');
            this.navCtrl.navigateForward(['accounts/add'], navigationExtras);
          } else {
            await this.navCtrl.navigateRoot('app/add-files');
          }
        });
    } catch (error) {
      console.log('error =====>', error);
    }
  }
}
